import {
  EChecklistTaskStatus,
  ETaskId,
  TaskCategories
} from 'common/contracts';
import { setupChecklistStateService } from 'state/setup-checklist.state.service';

import { ISetupChecklistCategory, ISetupChecklistTaskAction } from './types';
import { checkTaskPermission } from './utils';

const PAYMENT_LINKS_DOCS_URL = 'https://docs.appcharge.com/guides/payment-links/about-payment-links';
const PLAYER_AUTH_DOCS_URL = 'https://docs.appcharge.com/guides/webstore/player-authentication/about-player-authentication';
const ORDER_REPORTING_DOCS_URL = 'https://docs.appcharge.com/guides/checkout/orders/about-player-order-reporting';
const PERSONALIZATION_DOCS_URL = 'https://docs.appcharge.com/guides/webstore/personalization/about-personalization';
const CUSTOM_DOMAIN_DOCS_URL = 'https://docs.appcharge.com/guides/webstore/design/set-up-a-custom-domain';
const CHECKOUT_SESSION_DOCS_URL = 'https://docs.appcharge.com/api-reference/checkout/checkout-session/create-checkout-session';
const FRONTEND_SDK_DOCS_URL = 'https://docs.appcharge.com/sdks/checkout/frontend-sdk/about-frontend-integration';
const PRICE_POINTS_DOCS_URL = 'https://docs.appcharge.com/sdks/checkout/frontend-sdk/set-up-price-points';

export const getCategoryStatus = (
  taskStatuses: EChecklistTaskStatus[]
): EChecklistTaskStatus => {
  const unique = Array.from(new Set(taskStatuses));

  if (unique.length === 1) return unique[0];

  if (unique.every((status) => status === EChecklistTaskStatus.OPEN))
    return EChecklistTaskStatus.OPEN;
  if (unique.every((status) => status === EChecklistTaskStatus.PENDING))
    return EChecklistTaskStatus.PENDING;
  if (unique.every((status) => status === EChecklistTaskStatus.DONE))
    return EChecklistTaskStatus.DONE;

  if (unique.includes(EChecklistTaskStatus.OPEN))
    return EChecklistTaskStatus.IN_PROGRESS;
  if (
    unique.every(
      (status) =>
        status === EChecklistTaskStatus.DONE ||
        status === EChecklistTaskStatus.PENDING
    )
  )
    return EChecklistTaskStatus.PENDING;

  return EChecklistTaskStatus.IN_PROGRESS; // fallback for unexpected combinations
};

export const addPermissionBasedLocking = (
  categories: ISetupChecklistCategory[],
  publisherId: string
): ISetupChecklistCategory[] => {
  return categories.map(category => ({
    ...category,
    tasks: category.tasks.map(task => ({
      ...task,
      locked: task.action ? !checkTaskPermission(task.id, publisherId) : false
    }))
  }));
};

export const selfServeChecklistInitialCategories: ISetupChecklistCategory[] = [
  {
    title: TaskCategories.CHECKOUT,
    tasks: [
      {
        id: ETaskId.EDIT_CHECKOUT_IN_BUILDER,
        title: 'Open Builder tab & edit the Checkout',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/theme/checkout`);
        }
      }
    ]
  },
  {
    title: TaskCategories.VERIFY_BUSINESS,
    tasks: [
      {
        id: ETaskId.VERIFY_BUSINESS_CONTACT,
        title: 'Submit your company contact info',
        action: ({ toggleChecklist }) => {
          toggleChecklist();
          setupChecklistStateService.setIsVerifyBusinessModalOpen(true);
        }
      }
    ]
  },
  {
    title: TaskCategories.DOCUMENTATION,
    tasks: [
      {
        id: ETaskId.READ_DOCUMENTATION,
        title: 'Learn more about the checkout',
        action: ({ markTaskAsDone }) => {
          window.open(PAYMENT_LINKS_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      }
    ]
  },
  {
    title: TaskCategories.TEST_PAYMENTS,
    tasks: [
      {
        id: ETaskId.TEST_PAYMENTS,
        title: 'Make an order and test payments',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/settings/integration`);
        }
      }
    ]
  },
  {
    title: TaskCategories.SIGN_AGREEMENT,
    tasks: [
      {
        id: ETaskId.SIGN_AGREEMENT,
        title: 'Review and sign the Appcharge Agreement',
        action: ({ toggleChecklist }) => {
          toggleChecklist();
          setupChecklistStateService.setIsSendAgreementModalOpen(true);
        }
      }
    ]
  },
  {
    title: TaskCategories.GO_LIVE,
    tasks: [
      {
        id: ETaskId.GO_LIVE,
        title: 'Go live with checkout'
      }
    ],
    tooltip: 'Complete mandatory steps to unlock production.'
  }
];

export const webStoreChecklistInitialCategories: ISetupChecklistCategory[] = [
  {
    title: TaskCategories.BUILDER,
    tasks: [
      {
        id: ETaskId.CUSTOMIZE_GENERAL_STORE_SETTINGS,
        title: 'Customize your web store layout',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/theme/general`);
        }
      },
      {
        id: ETaskId.CUSTOMIZE_STORE_PAGE,
        title: 'Design your store page',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/theme/store`);
        }
      },
      {
        id: ETaskId.CUSTOMIZE_CHECKOUT,
        title: 'Design your checkout page',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/theme/checkout`);
        }
      },
      {
        id: ETaskId.CUSTOMIZE_SUCCESS_PAGE,
        title: 'Design your checkout success page',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/theme/completed`);
        }
      }
    ]
  },
  {
    title: TaskCategories.AUTHENTICATION,
    tasks: [
      {
        id: ETaskId.REVIEW_AUTH_API_DOCS,
        title: 'Review our player authentication documentation',
        action: ({ markTaskAsDone }) => {
          window.open(PLAYER_AUTH_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      },
      {
        id: ETaskId.CUSTOMIZE_AUTH_API,
        title: 'Add your Authenticate Player Callback endpoint to configure player authentication for your web store',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/settings/integration`);
        }
      },
      {
        id: ETaskId.CONFIGURE_SIGNIN_METHODS,
        title: 'Choose how players sign in to your web store',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/settings/playerAuth`);
        }
      },
      {
        id: ETaskId.CUSTOMIZE_LOGIN_PAGE,
        title: 'Design your login page',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/theme/login`);
        }
      }
    ]
  },
  {
    title: TaskCategories.PLAYER_ORDER_REPORTING_INTEGRATION,
    tasks: [
      {
        id: ETaskId.REVIEW_ORDER_REPORTING_DOCS,
        title: 'Review our player order reporting documentation',
        action: ({ markTaskAsDone }) => {
          window.open(ORDER_REPORTING_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      },
      {
        id: ETaskId.SETUP_AWARD_API,
        title: 'Add your Grant Awards Callback endpoint to configure awards for your customers',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/settings/integration`);
        }
      }
    ]
  },
  {
    title: TaskCategories.OFFERS,
    tasks: [
      {
        id: ETaskId.CREATE_FIRST_PRODUCT,
        title: 'Add products to your web store catalog',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/products`);
        }
      },
      {
        id: ETaskId.CREATE_OFFER_DESIGN,
        title: 'Design your bundle offer',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/offersui`);
        }
      },
      {
        id: ETaskId.ACTIVATE_FIRST_BUNDLE,
        title: 'Create your bundle offer ',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/bundles`);
        }
      }
    ]
  },
  {
    title: TaskCategories.PRICE_POINTS,
    tasks: [
      {
        id: ETaskId.CREATE_PRICE_POINTS,
        title: 'Define localized price points for your offers',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/pricing/points`);
        }
      }
    ]
  },
  {
    title: TaskCategories.PERSONALIZATION_API,
    tasks: [
      {
        id: ETaskId.REVIEW_PERSONALIZATION_DOCS,
        title: 'Review our personalization documentation',
        action: ({ markTaskAsDone }) => {
          window.open(PERSONALIZATION_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      },
      {
        id: ETaskId.SETUP_PERSONALIZATION_API,
        title: 'Add your Personalize Web Store Callback endpoint to configure personalization for your web store',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/settings/integration`);
        }
      }
    ]
  },
  {
    title: TaskCategories.GOING_LIVE,
    tasks: [
      {
        id: ETaskId.REVIEW_DOMAIN_DOCS,
        title: 'Review our custom domain documentation',
        action: ({ markTaskAsDone }) => {
          window.open(CUSTOM_DOMAIN_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      },
      {
        id: ETaskId.CUSTOMIZE_YOUR_DOMAIN,
        title: 'Add your custom domain for your web store',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/settings/company`);
        }
      }
    ]
  }
];

export const webCheckoutChecklistInitialCategories: ISetupChecklistCategory[] = [
  {
    title: TaskCategories.BUILDER,
    tasks: [
      {
        id: ETaskId.CUSTOMIZE_CHECKOUT,
        title: 'Design your checkout page',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/theme/checkout`);
        }
      },
      {
        id: ETaskId.CUSTOMIZE_RECEIPT,
        title: 'Design your customer receipts',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/theme/checkout`);
        }
      }
    ]
  },
  {
    title: TaskCategories.CREATE_CHECKOUT_SESSION_API,
    tasks: [
      {
        id: ETaskId.REVIEW_CHECKOUT_SESSION_DOCS,
        title: 'Review our checkout documentation',
        action: ({ markTaskAsDone }) => {
          window.open(CHECKOUT_SESSION_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      }
    ]
  },
  {
    title: TaskCategories.FRONTEND_SDK,
    tasks: [
      {
        id: ETaskId.REVIEW_FE_SDK_DOCS,
        title: 'Review our Frontend SDK documentation',
        action: ({ markTaskAsDone }) => {
          window.open(FRONTEND_SDK_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      }
    ]
  },
  {
    title: TaskCategories.PLAYER_ORDER_REPORTING_INTEGRATION,
    tasks: [
      {
        id: ETaskId.REVIEW_AWARD_API_DOCS,
        title: 'Review our player order reporting documentation',
        action: ({ markTaskAsDone }) => {
          window.open(ORDER_REPORTING_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      },
      {
        id: ETaskId.SETUP_AWARD_API,
        title: 'Add your Grant Awards Callback endpoint to configure awards for your customers',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/settings/integration`);
        }
      }
    ]
  },
  {
    title: TaskCategories.PRICE_POINTS,
    tasks: [
      {
        id: ETaskId.CREATE_PRICE_POINTS,
        title: 'Define localized price points for your offers',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/pricing/points`);
        }
      },
      {
        id: ETaskId.USE_GET_PRICE_POINTS,
        title: 'Get your price points',
        action: ({ markTaskAsDone }) => {
          window.open(PRICE_POINTS_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      }
    ]
  },
  {
    title: TaskCategories.GOING_LIVE,
    tasks: [
      {
        id: ETaskId.REVIEW_DOMAIN_DOCS,
        title: 'Review our custom domain documentation',
        action: ({ markTaskAsDone }) => {
          window.open(CUSTOM_DOMAIN_DOCS_URL, '_blank');
          markTaskAsDone();
        }
      },
      {
        id: ETaskId.CUSTOMIZE_YOUR_DOMAIN,
        title: 'Add your custom domain for your Checkout page',
        action: ({ navigate, publisherId }: ISetupChecklistTaskAction) => {
          navigate(`/project/${publisherId}/settings/company`);
        }
      }
    ]
  }
]
